import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, ArrowRight } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking"); // checking | success | error
  const sessionId = params.get("session_id");

  const poll = useCallback(async (attempt) => {
    if (attempt > 8) {
      setState("error");
      return;
    }
    try {
      const { data } = await api.get(`/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        await refresh();
        setState("success");
        toast.success("Your account is activated!");
      } else if (["failed", "expired"].includes(data.payment_status)) {
        setState("error");
      } else {
        setTimeout(() => poll(attempt + 1), 2000);
      }
    } catch (e) {
      setTimeout(() => poll(attempt + 1), 2000);
    }
  }, [sessionId, refresh]);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    poll(0);
  }, [sessionId, poll]);

  return (
    <div className="min-h-screen bg-background afha-grain flex flex-col" data-testid="payment-success-page">
      <header className="bg-white border-b border-border">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 h-[68px] flex items-center"><Logo /></div>
      </header>
      <main className="flex-1 flex items-center justify-center p-6">
        <div className="bg-white border border-border rounded-xl p-10 max-w-md w-full text-center">
          {state === "checking" && (
            <>
              <Loader2 className="h-12 w-12 text-navy animate-spin mx-auto mb-4" />
              <h1 className="font-display text-2xl text-navy">Confirming your payment…</h1>
              <p className="text-muted-foreground mt-2">This only takes a moment.</p>
            </>
          )}
          {state === "success" && (
            <>
              <div className="h-16 w-16 rounded-full bg-patriot/10 flex items-center justify-center mx-auto mb-4"><CheckCircle2 className="h-9 w-9 text-patriot" /></div>
              <h1 className="font-display text-2xl text-navy">Account activated!</h1>
              <p className="text-muted-foreground mt-2">Your full AFHA back office is now unlocked.</p>
              <Button onClick={() => navigate("/dashboard")} data-testid="success-continue" className="mt-6 w-full rounded-full bg-patriot hover:bg-patriot-dark text-white gap-2">
                Go to my dashboard <ArrowRight className="h-4 w-4" />
              </Button>
            </>
          )}
          {state === "error" && (
            <>
              <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4"><XCircle className="h-9 w-9 text-muted-foreground" /></div>
              <h1 className="font-display text-2xl text-navy">Payment not completed</h1>
              <p className="text-muted-foreground mt-2">We couldn't confirm your payment. You can try again anytime.</p>
              <Button onClick={() => navigate("/activate")} className="mt-6 w-full rounded-full bg-navy hover:bg-navy-dark text-white">Back to activation</Button>
            </>
          )}
        </div>
      </main>
    </div>
  );
}
